import type { TelemetryDataPoint, TelemetryNote } from '@/types';

const FONT_FAMILY =
  "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif";

export const CHART_CONSTANTS = {
  COLORS: {
    MIN_MARKER: '#dc2626',
    MAX_MARKER: '#16a34a',
    WHITE_BORDER: '#1a1a2e',
  },
  MARKER: {
    RADIUS: 4,
    BORDER_WIDTH: 2,
    LABEL_OFFSET: 8,
  },
  FONT_FAMILY,
  FONT: `bold 11px ${FONT_FAMILY}`,
  NOTE_HIT_RADIUS: 12,
  NOTE_GROUPING_THRESHOLD_PX: 20,
} as const;

export const noteIconFont = (size: number): string =>
  `bold ${size}px ${FONT_FAMILY}`;

export interface ChartMargins {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

export interface ChartLayout {
  margins: ChartMargins;
  lapBarHeight: number;
}

export interface LapTiming {
  lapNumber: number;
  startTime?: number;
  endTime?: number;
  lapTime: number;
}

export interface NoteGroup {
  x: number;
  notes: TelemetryNote[];
}

export const getLapBarHeight = (lapData?: LapTiming[] | null): number =>
  lapData && lapData.length > 0 ? 40 : 0;

export const getChartLayout = (lapBarHeight: number): ChartLayout => ({
  lapBarHeight,
  margins: { top: 30 + lapBarHeight, right: 30, bottom: 60, left: 40 },
});

export const getFullTimeDomain = (
  data: TelemetryDataPoint[]
): [number, number] => [
  Math.min(...data.map((d) => d.timestamp)),
  Math.max(...data.map((d) => d.timestamp)),
];

export const findNoteAtMouse = (
  notes: TelemetryNote[],
  mouseX: number,
  mouseY: number,
  domain: [number, number],
  margins: ChartMargins,
  chartWidth: number,
  markerRadius: number
): TelemetryNote | null => {
  const markerY = margins.top - 12;
  for (const note of notes) {
    if (note.timeOffset < domain[0] || note.timeOffset > domain[1]) continue;

    const noteX =
      margins.left +
      ((note.timeOffset - domain[0]) / (domain[1] - domain[0])) * chartWidth;

    if (
      Math.abs(mouseX - noteX) <= markerRadius &&
      Math.abs(mouseY - markerY) <= markerRadius
    ) {
      return note;
    }
  }
  return null;
};

export const groupNotesByX = (
  notes: TelemetryNote[],
  noteToX: (note: TelemetryNote) => number,
  isOwnGroup?: (note: TelemetryNote) => boolean
): NoteGroup[] =>
  notes.reduce((groups, note) => {
    const x = noteToX(note);

    if (isOwnGroup?.(note)) {
      groups.push({ x, notes: [note] });
    } else {
      const existingGroup = groups.find(
        (group) =>
          Math.abs(group.x - x) < CHART_CONSTANTS.NOTE_GROUPING_THRESHOLD_PX
      );

      if (existingGroup) {
        existingGroup.notes.push(note);
      } else {
        groups.push({ x, notes: [note] });
      }
    }

    return groups;
  }, [] as NoteGroup[]);

export const findClosestPoint = (
  data: TelemetryDataPoint[],
  timestamp: number
): TelemetryDataPoint => {
  if (data.length === 0) {
    throw new Error('findClosestPoint requires a non-empty data array');
  }

  let low = 0;
  let high = data.length - 1;
  let closestIndex = 0;

  while (low <= high) {
    const mid = (low + high) >>> 1;
    const midDistance = Math.abs(data[mid].timestamp - timestamp);
    const closestDistance = Math.abs(data[closestIndex].timestamp - timestamp);

    if (
      midDistance < closestDistance ||
      (midDistance === closestDistance &&
        (data[mid].timestamp < data[closestIndex].timestamp ||
          (data[mid].timestamp === data[closestIndex].timestamp &&
            mid < closestIndex)))
    ) {
      closestIndex = mid;
    }

    if (data[mid].timestamp < timestamp) {
      low = mid + 1;
    } else {
      high = mid - 1;
    }
  }

  return data[closestIndex];
};
